import { DownOutlined } from "@ant-design/icons";
import { Dropdown, MenuProps, Modal } from "antd";
import * as React from "react";
import { ZSpecCategoryOption } from "src/business/specification";
import {
  ZSecSpecRow,
  zSecSpecRow,
} from "src/business/specification/ZSecSpecRow";
import { onError } from "src/common/onError";
import { MdmTable } from "src/components/MdmTable";
import { fullRtLoader } from "src/components/ReactiveTable/loaders";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { ToolBox2 } from "src/components/ToolBox2";

import { ZSpecsSelectTable } from "../ZAttrComponent";
import * as styles from "./AttrSpecsSelectTable.module.less";
import { DeletedSpecsWarning } from "./DeletedSpecsWarning";
import { makeSecColumns } from "./secColumns";
import { SecSpecSelectModal } from "./SecSpecSelectModal";
import { SecSpecSelectStore } from "./SecSpecSelectModal/SecSpecSelectStore";

type PropsAttrSpecsSelectTable = {
  value: unknown;
  onChange?(newValue: unknown): void;
  disabled?: boolean;
  component: ZSpecsSelectTable;
  onBlur(): void;
};

// было прибито гвоздями, должно приходить с бэка
const linkTypeList: ZSpecCategoryOption[] = [
  { code: 0, label: "Прямая" },
  { code: 1, label: "Трансляция" },
];

export const AttrSpecsSelectTable: React.FC<PropsAttrSpecsSelectTable> = (
  props,
) => {
  const { value, onChange, component, disabled, onBlur } = props;
  const [modal, modalContextHolder] = Modal.useModal();
  const [specSelectStore, setSpecSelectStore] =
    React.useState<SecSpecSelectStore | null>(null);

  const specsList = React.useMemo(() => {
    const parsed = zSecSpecRow.array().nullable().safeParse(value);
    const specs: ZSecSpecRow[] = (parsed.success ? parsed.data : null) ?? [];
    if (parsed.error) onError(parsed.error);
    return specs;
  }, [value]);

  const tableStore = React.useMemo(
    () =>
      new RtStore<ZSecSpecRow, {}>({
        rowKey: "id",
        loader: fullRtLoader(async () => specsList),
      }),
    [specsList],
  );

  const onDelete = (row: ZSecSpecRow) => {
    onBlur();
    const newValue = specsList.filter((spec) => spec.id !== row.id);

    modal.confirm({
      title: "Подтвердите изменения",
      content: <DeletedSpecsWarning deletedSpec={row} />,
      okText: "Подтвердить",
      cancelText: "Отменить",
      onOk: () => onChange?.(newValue),
    });
  };

  const onAdd = (spec: ZSecSpecRow) => {
    onChange?.([...specsList, spec]);
  };

  const menuItems: MenuProps["items"] = React.useMemo(
    () =>
      linkTypeList.map((linkType) => ({
        key: String(linkType.code),
        label: linkType.label,
        onClick: () => {
          onBlur();
          setSpecSelectStore(
            new SecSpecSelectStore(
              component.classId,
              linkType,
              component.prohibitedNames,
            ),
          );
        },
      })),
    [],
  );

  return (
    <div className={styles.box}>
      {modalContextHolder}

      <ToolBox2
        mainTool={<div className={styles.title}>Вторичные спецификации</div>}
        extraTools={
          <div>
            {!disabled && (
              <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                <div className={styles.dropdownButton}>
                  <span>Добавить связь со спецификацией</span>
                  <DownOutlined />
                </div>
              </Dropdown>
            )}
          </div>
        }
      >
        <MdmTable
          store={tableStore}
          columns={makeSecColumns(!!disabled, onDelete)}
          resizableColumns
          components={{ Footer: () => null }}
        />
      </ToolBox2>

      {specSelectStore && (
        <SecSpecSelectModal
          store={specSelectStore}
          open
          close={() => {
            setSpecSelectStore(null);
          }}
          onFinish={() => {
            const row = specSelectStore.getResult();
            if (row) {
              onAdd({
                ...row,
                linkType: specSelectStore.linkType,
              });
            }
            setSpecSelectStore(null);
          }}
        />
      )}
    </div>
  );
};
